import { useCallback, useEffect, useMemo, useRef, type TouchEvent } from "react";
import { API_BASE } from "./config";
import type { PhotoItem } from "./types";

type CompFullscreenViewerProps = {
  photo: PhotoItem | null;
  photos: PhotoItem[];
  onPhotoChange: (photo: PhotoItem) => void;
  onClose: () => void;
};

const SWIPE_THRESHOLD_PX = 50;

export function CompFullscreenViewer({
  photo,
  photos,
  onPhotoChange,
  onClose,
}: CompFullscreenViewerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const touchStartX = useRef<number | null>(null);

  const index = useMemo(
    () => (photo ? photos.findIndex((p) => p.id === photo.id) : -1),
    [photo, photos],
  );
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < photos.length - 1;

  const goPrev = useCallback(() => {
    if (!photo || index <= 0) return;
    onPhotoChange(photos[index - 1]!);
  }, [photo, index, photos, onPhotoChange]);

  const goNext = useCallback(() => {
    if (!photo || index < 0 || index >= photos.length - 1) return;
    onPhotoChange(photos[index + 1]!);
  }, [photo, index, photos, onPhotoChange]);

  useEffect(() => {
    if (!photo) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [photo, onClose, goPrev, goNext]);

  useEffect(() => {
    if (!photo) return;
    if (photo.media_kind === "video") {
      return () => videoRef.current?.pause();
    }
  }, [photo]);

  const viewerOpen = photo != null;
  useEffect(() => {
    if (!viewerOpen) return;

    const blockBehind = (e: Event) => {
      if (e.target instanceof Element && e.target.closest("video")) return;
      e.preventDefault();
    };

    const opts = { capture: true, passive: false } as const;
    document.addEventListener("wheel", blockBehind, opts);
    document.addEventListener("touchmove", blockBehind, opts);

    return () => {
      document.removeEventListener("wheel", blockBehind, opts);
      document.removeEventListener("touchmove", blockBehind, opts);
    };
  }, [viewerOpen]);

  if (!photo) return null;

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
    const dx = endX - touchStartX.current;
    touchStartX.current = null;
    if (dx > SWIPE_THRESHOLD_PX) goPrev();
    else if (dx < -SWIPE_THRESHOLD_PX) goNext();
  };

  /** Equal width & height; matches grid `auto` columns */
  const chromeBtnBase =
    "box-border flex size-14 shrink-0 items-center justify-center rounded-lg text-white touch-manipulation sm:size-16";
  const arrowBtnClass = `${chromeBtnBase} hover:bg-white/10 active:bg-white/15`;
  const closeBtnClass = `${chromeBtnBase} z-30 hover:bg-white/20`;

  return (
    <div
      className="fixed inset-0 z-50 flex min-h-0 min-w-0 flex-col bg-black/95 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Vollbild anzeigen"
    >
      <div className="grid flex-1 min-h-0 min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] grid-rows-[auto_minmax(0,1fr)] isolate">
        {/* Full grid: media uses entire 2×3 area; chrome layers above */}
        <div
          className="col-start-1 row-start-1 col-span-3 row-span-2 relative z-0 flex h-full min-h-0 w-full min-w-0 items-center justify-center"
          onClick={onClose}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="flex h-full min-h-0 w-full min-w-0 items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {photo.media_kind === "video" ? (
              <video
                ref={videoRef}
                src={`${API_BASE}${photo.url}`}
                className="h-full min-h-0 w-full min-w-0 object-contain object-center"
                controls
                autoPlay
                playsInline
                controlsList="nodownload nofullscreen"
              />
            ) : (
              <img
                src={`${API_BASE}${photo.url}`}
                alt={photo.file_name}
                className="h-full min-h-0 w-full min-w-0 object-contain object-center select-none"
                draggable={false}
              />
            )}
          </div>
          <p className="pointer-events-none absolute bottom-3 left-1/2 z-[1] max-w-[min(100%,42rem)] -translate-x-1/2 truncate px-4 text-center text-sm text-white/80">
            {photo.file_name}
          </p>
        </div>
        {/* (1,1) */}
        <div className="pointer-events-none col-start-1 row-start-1" aria-hidden />
        {/* (1,2) */}
        <div className="pointer-events-none col-start-2 row-start-1" aria-hidden />
        {/* (1,3) close — wrapper ignores hits so swipes pass through; only the button is interactive */}
        <div className="pointer-events-none z-10 col-start-3 row-start-1 flex items-start justify-end self-start justify-self-end">
          <button
            type="button"
            onClick={onClose}
            className={`${closeBtnClass} pointer-events-auto`}
            aria-label="Vollbild schließen"
          >
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* (2,1) prev */}
        <div className="pointer-events-none z-10 col-start-1 row-start-2 flex min-h-0 min-w-0 items-center justify-center">
          {hasPrev ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className={`${arrowBtnClass} pointer-events-auto`}
              aria-label="Vorheriges Medium"
            >
              <svg className="h-10 w-10 drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          ) : null}
        </div>
        {/* (2,2) intentionally empty — clicks pass through to full-grid media layer */}
        {/* (2,3) next */}
        <div className="pointer-events-none z-10 col-start-3 row-start-2 flex min-h-0 min-w-0 items-center justify-center">
          {hasNext ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className={`${arrowBtnClass} pointer-events-auto`}
              aria-label="Nächstes Medium"
            >
              <svg className="h-10 w-10 drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
